// AuthContext with proper TypeScript
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'
import { Session, User } from '@supabase/supabase-js'
import { Capacitor } from '@capacitor/core'
import { useLocation, useNavigate } from 'react-router-dom'
import { withRetry } from '../utils/dbRetry'
import { useTrial } from '../hooks/useTrial'
import { useSubscription } from '../hooks/useSubscription'
import { useAuthRedirect } from '../hooks/useAuthRedirect'

// TypeScript declaration for Google Ads conversion tracking
declare global {
  interface Window {
    trackPurchaseConversion?: (transactionId?: string) => void;
  }
}

// Keep the original TS check disabled until we can properly fix the type issues
interface AuthContextType {
  user: User | null
  session: Session | null
  hasSubscription: boolean
  isTrial: boolean
  trialEndDate: string | null
  trialDaysLeft: number | null
  loading: boolean
  logout: () => Promise<void>
  clearSession: () => void
  updateSubscription: (value: boolean) => void
  setUser: (user: User | null) => void
  setSession: (session: Session | null) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  
  // Initialize subscription status from localStorage for persistence
  const [hasSubscription, setHasSubscription] = useState<boolean>(() => {
    const stored = localStorage.getItem('hasSubscription')
    return stored === 'true'
  })
  
  // Add trial states
  const [isTrial, setIsTrial] = useState<boolean>(() => {
    const stored = localStorage.getItem('isTrial')
    return stored === 'true'
  })
  const [trialEndDate, setTrialEndDate] = useState<string | null>(() => {
    return localStorage.getItem('trialEndDate')
  })

  const [loading, setLoading] = useState(true)
  const [subscriptionChecking, setSubscriptionChecking] = useState(false)

  // Callback for subscription hook to update subscription state
  const handleSubscriptionChange = useCallback((value: boolean) => {
    setHasSubscription(value)
    localStorage.setItem('hasSubscription', value ? 'true' : 'false')
  }, [])
  
  // Callback for trial hook to update trial state
  const handleTrialChange = useCallback((isActive: boolean, endDate: string | null) => {
    setIsTrial(isActive)
    setTrialEndDate(endDate)
    localStorage.setItem('isTrial', isActive ? 'true' : 'false')
    if (endDate) {
      localStorage.setItem('trialEndDate', endDate)
    } else {
      localStorage.removeItem('trialEndDate')
    }
  }, [])

  // Callback for subscription hook to update loading states
  const handleLoadingChange = useCallback((checking: boolean, loading: boolean) => {
    setSubscriptionChecking(checking)
    setLoading(loading)
  }, [])

  // Use trial hook for trial management
  const { trialDaysLeft, updateTrialInDatabase } = useTrial({
    user,
    isTrial,
    trialEndDate,
    onTrialChange: handleTrialChange
  })

  // Use subscription hook for subscription management
  const { fetchSubscription, pollingCleanup } = useSubscription({
    user,
    session,
    onSubscriptionChange: handleSubscriptionChange,
    onTrialChange: handleTrialChange,
    onLoadingChange: handleLoadingChange,
    updateTrialInDatabase
  })

  // Wrapper functions to maintain context API compatibility
  const updateTrialStatus = (isActive: boolean, endDate: string | null) => {
    handleTrialChange(isActive, endDate)
  }

  const updateSubscription = (value: boolean) => {
    handleSubscriptionChange(value)
  }

  // Use redirect hook for auth-based navigation
  useAuthRedirect({
    loading,
    user,
    hasSubscription,
    isTrial,
    pathname: location.pathname,
    navigate
  })

  // IMPORTANT: Always keep loading true when checking subscription
  useEffect(() => {
    if (subscriptionChecking) {
      setLoading(true)
    }
  }, [subscriptionChecking])


  // SECURITY: Debug mode only in development environment and native platforms
  useEffect(() => {
    const isDevelopment = process.env.NODE_ENV === 'development' || 
                         window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1'
    
    if (isDevelopment && Capacitor.isNativePlatform() && localStorage.getItem('debug_mode') === 'true') {
      try {
        const debugUser = JSON.parse(localStorage.getItem('debug_user') || '{}')
        // SECURITY: Only allow debug mode in development with valid debug user structure
        if (debugUser.id && debugUser.email) {
          setUser(debugUser)
          updateSubscription(true)
          setLoading(false)
          return // Skip normal auth flow
        }
      } catch (error) {
        // Continue with normal flow if debug mode fails
      }
    }
    
    // Normal authentication flow continues below...
    
    const initializeAuth = async () => {
      try {
        // ENHANCED: Check stored subscription first with proper trial expiration
        const storedSubscription = localStorage.getItem('hasSubscription') === 'true'
        const storedTrial = localStorage.getItem('isTrial') === 'true'
        const storedTrialEnd = localStorage.getItem('trialEndDate')

        if (storedSubscription) {
          updateSubscription(true)
        }

        // FIXED: Check trial expiration immediately on startup
        if (storedTrial && storedTrialEnd) {
          const trialEnd = new Date(storedTrialEnd)
          const now = new Date()

          if (trialEnd > now) {
            setIsTrial(true)
          } else {
            // Clear expired trial from localStorage
            updateTrialStatus(false, null)
            // Note: Database will be updated when session is available
          }
        }
        
        const { data, error } = await supabase.auth.getSession()
        
        if (error) {
          throw error
        }
        
        if (data?.session) {
          setSession(data.session)
          setUser(data.session.user)
          
          // Check subscription status
          if (data.session.user.email) {
            // IMPORTANT: Keep loading true during subscription check
            setSubscriptionChecking(true)
            await fetchSubscription(data.session.user.email)
          } else {
            updateSubscription(false)
            setSubscriptionChecking(false)
            setLoading(false)
          }
        } else {
          updateSubscription(false)
          updateTrialStatus(false, null)
          setSubscriptionChecking(false)
          setLoading(false)
        }
      } catch (err) {
        updateSubscription(false)
        updateTrialStatus(false, null)
        setSubscriptionChecking(false)
        setLoading(false)
      }
    }
    
    initializeAuth()
    
    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        // Process meaningful auth changes
        if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
          if (newSession) {
            setSession(newSession)
            setUser(newSession.user)

            // Fetch subscription on initial session or sign in
            if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN') && newSession.user?.email) {
              setSubscriptionChecking(true)
              await fetchSubscription(newSession.user.email)
            } else if (event === 'TOKEN_REFRESHED' && newSession.user?.email) {
              // Skip verification if tab was just hidden/shown to prevent reload behavior
              if (document.hidden || document.visibilityState === 'hidden') {
                return
              }
              // Verify subscription on token refresh to prevent stale data
              // Rate-limited to prevent excessive API calls
              const lastVerified = localStorage.getItem('lastSubscriptionVerified')
              const shouldVerify = !lastVerified || (Date.now() - parseInt(lastVerified)) > 1800000 // 30 minutes
              if (shouldVerify) {
                setSubscriptionChecking(true)
                await fetchSubscription(newSession.user.email)
              }
            } else if (event !== 'INITIAL_SESSION') {
              setSubscriptionChecking(false)
              setLoading(false)
            }
          } else {
            setSession(null)
            setUser(null)
            updateSubscription(false)
            updateTrialStatus(false, null)
            setSubscriptionChecking(false)
            setLoading(false)
          }
        }
      }
    )
    
    return () => {
      authListener?.subscription.unsubscribe()
      // Cleanup any active polling on unmount
      pollingCleanup()
    }
  }, [])

  const logout = async () => {
    try {
      await supabase.auth.signOut()
      clearSession()
      // Redirect to home after logout
      navigate('/')
    } catch (err) {
      // Still clear local session even if supabase signout fails
      clearSession()
      navigate('/')
    }
  }

  const clearSession = () => {
    // Clear any active polling
    pollingCleanup()

    setSession(null)
    setUser(null)
    updateSubscription(false)
    updateTrialStatus(false, null)
    setSubscriptionChecking(false)
    setLoading(false)
    // Clear all auth-related localStorage items
    localStorage.removeItem('hasSubscription')
    localStorage.removeItem('isTrial')
    localStorage.removeItem('trialEndDate')
  }

  const value = {
    user,
    session,
    hasSubscription,
    isTrial,
    trialEndDate,
    trialDaysLeft,
    loading,
    logout,
    clearSession,
    updateSubscription,
    setUser,
    setSession,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}